import React, { useState, useEffect } from 'react';
import { Printer, Loader2, X, FileText, Calendar } from 'lucide-react';
import { api, API_BASE } from '../utils/api';
import { formatDateThai, printReport, formatDays } from '../utils/helpers';

const LeaveReport = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [printLoading, setPrintLoading] = useState(false);
  
  // Modal State
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailReport, setDetailReport] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadReports();
  }, [dateRange, selectedEmployee]);

  const loadInitialData = async () => {
    try {
      // ⚠️ ข้อมูลพนักงานและโรงเรียน จะถูกกรองให้เฉพาะของโรงเรียนนี้อัตโนมัติจาก Backend
      const [employeesData, organizationData] = await Promise.all([
        api.getEmployees(),
        api.getOrganizations()
      ]);
      
      const activeEmployees = employeesData.filter(emp => 
        emp.is_active == 1 && 
        emp.employee_id !== 'ADMIN' && 
        emp.position !== 'ผู้ดูแลระบบ'
      );
      
      setEmployees(activeEmployees);
      setOrganization(organizationData[0] || null);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = {
        type: 'daily',
        date_from: dateRange.from,
        date_to: dateRange.to
      };
      
      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      // ⚠️ ดึงข้อมูลเฉพาะประวัติของโรงเรียนนี้
      const rawData = await api.getReports(params);
      
      const leaveData = rawData.filter(record => 
        record.employee_id !== 'ADMIN' && 
        record.status && 
        ['ลาป่วย', 'ลากิจ'].includes(record.status)
      );

      const groupedMap = {};
      
      leaveData.forEach(record => {
          const empId = record.employee_id;
          if (!groupedMap[empId]) {
              groupedMap[empId] = {
                  employee_id: empId,
                  first_name: record.first_name,
                  last_name: record.last_name,
                  department: record.department,
                  sick_days: 0,
                  sick_times: 0,
                  personal_days: 0,
                  personal_times: 0,
                  requests: []
              };
          }
          
          groupedMap[empId].requests.push(record);
          
          if (record.status === 'ลาป่วย') {
              groupedMap[empId].sick_times += 1;
              groupedMap[empId].sick_days += parseFloat(record.work_hours || 1); 
          } else if (record.status === 'ลากิจ') {
              groupedMap[empId].personal_times += 1;
              groupedMap[empId].personal_days += parseFloat(record.work_hours || 1);
          }
      });

      let summaryList = Object.values(groupedMap);
      
      summaryList.sort((a, b) => {
        const deptA = a.department || '';
        const deptB = b.department || '';
        if (deptA !== deptB) {
          return deptA.localeCompare(deptB, 'th');
        }
        return a.employee_id.localeCompare(b.employee_id);
      });
      
      setReports(summaryList);

    } catch (error) {
      console.error('Error loading reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (summaryRecord) => {
      setDetailReport(summaryRecord);
      setIsDetailModalOpen(true);
  };

  // --- Print 1: Summary Report ---
  const generatePrintReport = () => {
    if (reports.length === 0) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์');
      return;
    }
    setPrintLoading(true);

    const dateTitle = (dateRange.from === dateRange.to 
        ? formatDateThai(dateRange.from)
        : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`);

    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';

    const reportTitle = `<br>รายงานสรุปสถิติการลาป่วย/ลากิจ ${dateTitle}`;
    
    const tableHeaders = `
      <tr>
        <th rowspan="2" style="width: 5%;">ลำดับ</th>
        <th rowspan="2" style="width: 25%;">ชื่อ-นามสกุล</th>
        <th rowspan="2" style="width: 20%;">แผนก/กลุ่มสาระฯ</th>
        <th colspan="2" style="width: 25%;">ลาป่วย</th>
        <th colspan="2" style="width: 25%;">ลากิจ</th>
      </tr>
      <tr>
        <th>จำนวน (ครั้ง)</th>
        <th>จำนวน (วัน)</th>
        <th>จำนวน (ครั้ง)</th>
        <th>จำนวน (วัน)</th>
      </tr>
    `;
    
    const tableRows = reports.map((item, index) => `
      <tr>
        <td style="text-align: center;">${index + 1}</td>
        <td>${item.first_name} ${item.last_name}</td>
        <td>${item.department || '-'}</td>
        <td style="text-align: center;">${item.sick_times}</td>
        <td style="text-align: center;">${formatDays(item.sick_days)}</td>
        <td style="text-align: center;">${item.personal_times}</td>
        <td style="text-align: center;">${formatDays(item.personal_days)}</td>
      </tr>
    `).join('');

    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    const signatureSection = `
      <div class="signature-section" style="margin-top: 40px;">
        <table style="width: 100%; border: none; margin-bottom: 0;">
          <tr style="border: none; background-color: transparent;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none; background-color: transparent;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;

    const printStyles = getPrintStyles(); 

    const reportContent = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${signatureSection}
    `;

    printReport(reportContent, reportTitle);
    setPrintLoading(false);
  };

  // --- Print 2: Detail Report ---
  const generateDetailPrintReport = () => {
    if (!detailReport || detailReport.requests.length === 0) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์');
      return;
    }
    setPrintLoading(true);

    const emp = detailReport;
    const records = detailReport.requests;
    
    records.sort((a, b) => new Date(a.work_date) - new Date(b.work_date));

    const dateTitle = (dateRange.from === dateRange.to 
        ? formatDateThai(dateRange.from)
        : `${formatDateThai(dateRange.from)} - ${formatDateThai(dateRange.to)}`);

    const logoHtml = organization?.logo 
      ? `<img src="${API_BASE}${organization.logo}" alt="โลโก้หน่วยงาน" class="logo">`
      : '';

    const reportTitle = `<br>รายงานรายละเอียดการลาป่วย/ลากิจ<br> ${emp.first_name} ${emp.last_name} (${emp.department || '-'})<br>ช่วงวันที่ ${dateTitle}`;

    const summaryHtml = `
      <div style="font-size: 18pt; margin-bottom: 15px; display: flex; justify-content: space-between;">
        <span><strong>รวมลาป่วย:</strong> ${emp.sick_times} ครั้ง (${formatDays(emp.sick_days)} วัน)</span>
        <span><strong>รวมลากิจ:</strong> ${emp.personal_times} ครั้ง (${formatDays(emp.personal_days)} วัน)</span>
      </div>
    `;

    const tableHeaders = `
      <tr>
        <th style="width: 20%;">วันที่</th>
        <th style="width: 20%;">ประเภทการลา</th>
        <th style="width: 60%;">เหตุผล/หมายเหตุ</th>
      </tr>
    `;

    const tableRows = records.map(rec => `
      <tr>
        <td style="text-align: center;">${formatDateThai(rec.work_date)}</td>
        <td style="text-align: center;">${rec.status}</td>
        <td>${rec.attendance_notes || '-'}</td>
      </tr>
    `).join('');

    const statHeadName = organization?.report_stat_head_name || '........................................';
    const statHeadPos = organization?.report_stat_head_position || 'เจ้าหน้าที่งานสถิติ';
    const vpName = organization?.report_vp_name || '........................................';
    const vpPos = organization?.report_vp_position || 'รองผู้อำนวยการกลุ่มบริหารงานบุคคล';

    const signatureSection = `
      <div class="signature-section" style="margin-top: 40px;">
        <table style="width: 100%; border: none; margin-bottom: 0;">
          <tr style="border: none; background-color: transparent;">
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${statHeadName})</p>
              <p>${statHeadPos}</p>
            </td>
            <td style="border: none; text-align: center; width: 50%; vertical-align: top; padding: 0;">
              <p style="margin-bottom: 5px;">ลงชื่อ..........................................................</p>
              <p style="margin-bottom: 5px;">(${vpName})</p>
              <p>${vpPos}</p>
            </td>
          </tr>
          <tr style="border: none; background-color: transparent;">
             <td colspan="2" style="border: none; text-align: center; padding-top: 20px;">
                วันที่ ${formatDateThai(new Date())}
             </td>
          </tr>
        </table>
      </div>
    `;

    const printStyles = getPrintStyles();

    const detailContent = `
      <style>${printStyles}</style>
      <div class="header">
        ${logoHtml}
        <div class="org-name">${organization?.name || 'หน่วยงาน'}</div>
        <div class="date">${reportTitle}</div>
      </div>
      ${summaryHtml}
      <table>
        <thead>${tableHeaders}</thead>
        <tbody>${tableRows}</tbody>
      </table>
      ${signatureSection}
    `;

    printReport(detailContent, reportTitle);
    setPrintLoading(false);
  };

  const getPrintStyles = () => `
    @font-face {
      font-family: 'Sarabun';
      src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype');
      font-weight: 300;
      font-style: normal;
    }
    @font-face {
      font-family: 'Sarabun';
      src: url('${API_BASE}fonts/THSarabun-Bold.ttf') format('truetype');
      font-weight: 700;
      font-style: normal;
    }
    @page {
      size: A4 portrait;
      margin: 1cm;
    }
    body {
      font-family: 'Sarabun', sans-serif;
      font-size: 18pt; 
      color: #000;
    }
    .header {
      text-align: center;
      margin-bottom: 15px;
      border-bottom: 2px solid #000;
      padding-bottom: 10px;
      font-size: 18pt; 
    }
    .logo {
      max-width: 80px;
      max-height: 80px;
      margin: 0 auto 10px;
    }
    .org-name {
      font-weight: 700;
    }
    .date {
      font-weight: 700;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    th, td {
      border: 1px solid #000;
      padding: 6px 4px;   
      vertical-align: top;
      height: auto;       
      line-height: 1.3;
    }
    th {
      background-color: #f0f0f0;
      font-weight: 700;
      text-align: center;
    }
    td {
      text-align: left;
      word-break: break-word;
    }
    tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    @media print {
      body { margin: 0; }
      .header {
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
      }
    }
  `;

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">รายงานการลาป่วย/ลากิจ</h1>
        <button
          onClick={generatePrintReport}
          disabled={loading || reports.length === 0 || printLoading || !organization} 
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
        >
          {printLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          <span>พิมพ์รายงานสรุป</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">จากวันที่</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">บุคลากร</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">ทุกคน</option>
              {employees
                .map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>
                    {emp.employee_id} - {emp.first_name} {emp.last_name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
              <p className="text-sm text-gray-500">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-middle border-b">รหัส</th>
                    <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-middle border-b">ชื่อ-นามสกุล</th>
                    <th rowSpan="2" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-middle border-b">แผนก/กลุ่มสาระฯ</th>
                    <th colSpan="2" className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">ลาป่วย</th>
                    <th colSpan="2" className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">ลากิจ</th>
                  </tr>
                  <tr>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">ครั้ง</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">วัน</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">ครั้ง</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">วัน</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reports.length > 0 ? reports.map((record, index) => (
                    <tr 
                        key={`${record.employee_id}-${index}`} 
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(record)}
                        title="คลิกเพื่อดูรายละเอียดการลา"
                    >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.employee_id}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{record.first_name} {record.last_name}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{record.department || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-pink-600 font-medium text-center bg-pink-50/30">{record.sick_times}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-pink-600 font-medium text-center bg-pink-50/30">{formatDays(record.sick_days)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-purple-600 font-medium text-center bg-purple-50/30">{record.personal_times}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-purple-600 font-medium text-center bg-purple-50/30">{formatDays(record.personal_days)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        ไม่พบข้อมูลการลาในช่วงที่เลือก
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {reports.length > 0 && (
              <div className="mt-4 text-sm text-gray-600 flex justify-between items-center">
                <span>แสดงผลลัพธ์ {reports.length} รายการ (คลิกที่แถวเพื่อดูรายละเอียด)</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- Detail Modal --- */}
      {isDetailModalOpen && detailReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                รายละเอียดการลาป่วย/ลากิจ: {detailReport.first_name} {detailReport.last_name}
              </h3>
              <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto space-y-4">
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className='flex items-center gap-2 text-sm'>
                        <FileText className="h-4 w-4 text-blue-600" />
                        <strong>รหัสพนักงาน:</strong> {detailReport.employee_id}
                    </div>
                    <div className='flex items-center gap-2 text-sm'>
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <strong>ช่วงรายงาน:</strong> {formatDateThai(dateRange.from)} - {formatDateThai(dateRange.to)}
                    </div>
                </div>

                <div className="flex gap-4 mb-4">
                     <div className="bg-pink-50 text-pink-700 px-4 py-2 rounded-md text-sm border border-pink-100 flex-1 text-center">
                         <strong>รวมลาป่วย:</strong> {detailReport.sick_times} ครั้ง ({formatDays(detailReport.sick_days)} วัน)
                     </div>
                     <div className="bg-purple-50 text-purple-700 px-4 py-2 rounded-md text-sm border border-purple-100 flex-1 text-center">
                         <strong>รวมลากิจ:</strong> {detailReport.personal_times} ครั้ง ({formatDays(detailReport.personal_days)} วัน)
                     </div>
                </div>

              {detailReport.requests.length > 0 ? (
                <div className='overflow-x-auto'>
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ประเภทการลา</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เหตุผล/หมายเหตุ</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {detailReport.requests
                                .sort((a,b) => new Date(a.work_date) - new Date(b.work_date))
                                .map((req, idx) => (
                                <tr key={idx}>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{formatDateThai(req.work_date)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${req.status === 'ลาป่วย' ? 'bg-pink-100 text-pink-800' : 'bg-purple-100 text-purple-800'}`}>
                                            {req.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{req.attendance_notes || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                    ไม่พบรายละเอียดการลาป่วย/ลากิจ ในช่วงที่เลือก
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="px-4 py-2 rounded-md text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                ปิด
              </button>
              <button
                onClick={generateDetailPrintReport}
                disabled={detailReport.requests.length === 0 || printLoading} 
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
              >
                {printLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                <span>พิมพ์รายงานละเอียด</span>
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveReport;